import { NotificationDialog } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import i18next from 'i18next';

interface Mime {
  type: string;
  pattern: (number | undefined)[];
  format: string;
}

export type validFileType = keyof typeof validFileTypes;

const MIME_TYPE = Object.freeze({
  png: 'image/png',
  jpeg: 'image/jpeg',
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  csv: 'text/csv',
});

const validFileTypes = {
  [MIME_TYPE.jpeg]: {
    type: 'image/jpeg',
    pattern: [0xff, 0xd8, 0xff],
    format: 'JPG/JPEG',
  },
  [MIME_TYPE.png]: {
    type: 'image/png',
    pattern: [0x89, 0x50, 0x4e, 0x47],
    format: 'PNG',
  },
  [MIME_TYPE.pdf]: {
    type: 'application/pdf',
    pattern: [0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e],
    format: 'PDF',
  },
  [MIME_TYPE.xls]: {
    type: 'application/vnd.ms-excel',
    pattern: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
    format: 'XLS',
  },
  [MIME_TYPE.xlsx]: {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pattern: [0x50, 0x4b, 0x03, 0x04],
    format: 'XLSX',
  },
  [MIME_TYPE.doc]: {
    type: 'application/msword',
    pattern: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
    format: 'DOC',
  },
  [MIME_TYPE.docx]: {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    pattern: [0x50, 0x4b, 0x03, 0x04],
    format: 'DOCX',
  },
  [MIME_TYPE.txt]: {
    type: 'text/plain',
    pattern: [],
    format: 'TXT',
  },
  [MIME_TYPE.csv]: {
    type: 'text/csv',
    pattern: [],
    format: 'CSV',
  },
} satisfies Record<string, Mime>;

class ValidFileTypesState {
  public static init(mimeTypes: validFileType | validFileType[] | undefined): Record<string, Mime> {
    if (mimeTypes == null) {
      return validFileTypes;
    }
    if (Array.isArray(mimeTypes)) {
      const filteredValidFileTypes = mimeTypes.reduce<Record<string, Mime>>((acc, item) => {
        if (validFileTypes[item]) {
          acc[item] = validFileTypes[item];
        }
        return acc;
      }, {});

      return filteredValidFileTypes;
    } else {
      const filtered = {
        [mimeTypes]: validFileTypes[mimeTypes],
      };

      return filtered;
    }
  }
}

export class FileValidationService {
  private _validFileTypes: Record<string, Mime>;

  constructor(validFileTypes: validFileType | validFileType[] | undefined) {
    this._validFileTypes = ValidFileTypesState.init(validFileTypes);
  }

  public static init(mimeTypes: validFileType | validFileType[] | undefined) {
    return new FileValidationService(mimeTypes);
  }

  private getFormatList = () => {
    return Object.values(this._validFileTypes)
      .map(({ format }) => format)
      .join(', ');
  };

  public async isValidFiles(files: File[]) {
    const validFiles = await this.checkUploadedFiles(files);
    const msg = i18next.t(keyMap.settingsDocsFormFileExtensionErrorHint, {
      format: this.getFormatList(),
      interpolation: { escapeValue: false },
    });

    if (validFiles.some((file) => file === false) || validFiles.length === 0) {
      NotificationDialog.show(msg, 'error', {}, 10000);

      return false;
    }

    return true;
  }

  public isValidFileSize(file: File, maxSize: number) {
    return file.size <= maxSize;
  }

  private checkUploadedFiles = (files: File[]) => {
    return Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();

            reader.onloadend = (e) => {
              if (!e || !reader.result) {
                resolve(false);
                return;
              }

              const bytes = new Uint8Array(reader.result as ArrayBuffer);

              const validationResult = this.isValidFileType(bytes, file.type);

              resolve(validationResult);
            };

            reader.readAsArrayBuffer(file);
          }),
      ),
    );
  };

  private isValidFileType = (bytes: Uint8Array, fileType: string) => {
    const currentMimeType = this._validFileTypes[fileType];

    if (!currentMimeType) {
      return false;
    }

    //Note: We allow some specific formats to be used without byte checking due to complex validation.
    if (currentMimeType.pattern.length === 0) {
      return true;
    }

    return currentMimeType.pattern.every((p, i) => bytes[i] === p);
  };
}

const validImageFileTypes: validFileType[] = ['image/png', 'image/jpeg'];
const validGeneralFileTypes: validFileType[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
];

export const fileValidationService = new FileValidationService(validGeneralFileTypes);

export const imageFileValidationService = new FileValidationService(validImageFileTypes);
